import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProviderPageLayout from '../../components/ProviderPageLayout';
import { StepProgress } from '../../components/ui/StepProgress';
import { PasswordField } from '../../components/ui/PasswordField';
import { useAuth } from '../../auth/useAuth';
import { saveSignupDraft, loadSignupDraft, clearSignupDraft } from '../../utils/signupDraft';

function buildStoreAddressForSignup(storeAddress, storeCity, storeState, storeZip, storeCountry) {
  const countryLabel = storeCountry === 'US' ? 'United States' : 'Canada';
  return [storeAddress, storeCity, storeState, storeZip, countryLabel].filter(Boolean).join(', ');
}

const SIGNUP_STEPS = ['Device', 'Account', 'Store'];

const ProviderSignup = () => {
  const navigate = useNavigate();
  const { signUp } = useAuth();

  const savedStep = sessionStorage.getItem('signupStep');
  const [step, setStep] = useState(savedStep ? parseInt(savedStep, 10) : 1);
  const [deviceId, setDeviceId] = useState('');
  const [deviceCode, setDeviceCode] = useState('');
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (step > 1) {
      sessionStorage.setItem('signupStep', step.toString());
    }
  }, [step]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [storeName, setStoreName] = useState('');
  const [storeAddress, setStoreAddress] = useState('');
  const [storeCity, setStoreCity] = useState('');
  const [storeCountry, setStoreCountry] = useState('CA');
  const [storeState, setStoreState] = useState('');
  const [storeZip, setStoreZip] = useState('');
  const [storePhone, setStorePhone] = useState('');

  const persistDraft = (overrides = {}) => {
    saveSignupDraft({
      step,
      deviceId,
      deviceCode,
      email,
      password,
      confirmPassword,
      firstName,
      lastName,
      storeName,
      storeAddress,
      storeCity,
      storeCountry,
      storeState,
      storeZip,
      storePhone,
      ...overrides,
    });
  };

  useEffect(() => {
    const draft = loadSignupDraft();
    if (!draft) return;
    if (draft.step) setStep(Number(draft.step) || 1);
    if (draft.deviceId) setDeviceId(draft.deviceId);
    if (draft.deviceCode) setDeviceCode(draft.deviceCode);
    if (draft.email) setEmail(draft.email);
    if (draft.password) setPassword(draft.password);
    if (draft.confirmPassword) setConfirmPassword(draft.confirmPassword);
    if (draft.firstName) setFirstName(draft.firstName);
    if (draft.lastName) setLastName(draft.lastName);
    if (draft.storeName) setStoreName(draft.storeName);
    if (draft.storeAddress) setStoreAddress(draft.storeAddress);
    if (draft.storeCity) setStoreCity(draft.storeCity);
    if (draft.storeCountry) {
      setStoreCountry(draft.storeCountry === 'CA' || draft.storeCountry === 'US' ? draft.storeCountry : 'CA');
    }
    if (draft.storeState) setStoreState(draft.storeState);
    if (draft.storeZip) setStoreZip(draft.storeZip);
    if (draft.storePhone) setStorePhone(draft.storePhone);
  }, []);

  const validateDevice = async (e) => {
    e.preventDefault();
    setValidationError('');

    if (!deviceId || !deviceCode) {
      setValidationError('Please enter both your device ID and device code.');
      return;
    }

    const trimmedId = deviceId.trim();
    const trimmedCode = deviceCode.trim();

    if (!/^[a-z0-9]{4}$/i.test(trimmedId)) {
      setValidationError(
        'Device ID should be 4 characters — a mix of letters and numbers (e.g. a1d2).'
      );
      return;
    }

    if (!/^\d{8}$/.test(trimmedCode)) {
      setValidationError('Device code should be exactly 8 digits.');
      return;
    }

    try {
      const { default: authApi } = await import('../../services/authApi');
      const result = await authApi.validateDevice(trimmedId, trimmedCode);

      if (result.valid) {
        sessionStorage.setItem('providerDeviceId', trimmedId);
        sessionStorage.setItem('providerDeviceCode', trimmedCode);
        setStep(2);
        persistDraft({ step: 2, deviceId: trimmedId, deviceCode: trimmedCode });
      } else {
        setValidationError(result.error || 'Invalid device credentials. Contact your administrator.');
      }
    } catch (err) {
      setValidationError(err.message || 'Could not validate device. Please try again.');
    }
  };

  const handleAccountStep = (e) => {
    e.preventDefault();
    setValidationError('');

    if (!email.trim() || !firstName.trim() || !lastName.trim()) {
      setValidationError('Please complete all fields.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim().toLowerCase())) {
      setValidationError('Please enter a valid email address.');
      return;
    }

    if (password.length < 8) {
      setValidationError('Password must be at least 8 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setValidationError('Passwords do not match.');
      return;
    }

    setStep(3);
    persistDraft({ step: 3 });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationError('');

    if (!storeName.trim() || !storeAddress.trim() || !storeCity.trim() || !storeState.trim() || !storeZip.trim()) {
      setValidationError('Please complete all store details.');
      return;
    }

    if (storeCountry !== 'CA' && storeCountry !== 'US') {
      setValidationError('Store must be located in Canada or the United States.');
      return;
    }

    const trimmedEmail = email.trim().toLowerCase();
    const sessionDeviceId = sessionStorage.getItem('providerDeviceId') || deviceId;
    const sessionDeviceCode = sessionStorage.getItem('providerDeviceCode') || deviceCode;
    const cognitoAddress = buildStoreAddressForSignup(
      storeAddress.trim(),
      storeCity.trim(),
      storeState.trim(),
      storeZip.trim(),
      storeCountry.trim()
    );

    try {
      await signUp(
        trimmedEmail,
        password,
        trimmedEmail,
        firstName.trim(),
        lastName.trim(),
        cognitoAddress,
        sessionDeviceId,
        sessionDeviceCode,
        storeName.trim(),
        storeAddress.trim(),
        storeCity.trim(),
        storeCountry.trim(),
        storeState.trim(),
        storeZip.trim(),
        storePhone.trim()
      );

      sessionStorage.setItem('pendingUsername', trimmedEmail);
      sessionStorage.setItem('pendingEmail', trimmedEmail);
      sessionStorage.setItem('pendingSignupPassword', password);
      clearSignupDraft();
      sessionStorage.removeItem('signupStep');
      navigate('/provider/verify');
    } catch (err) {
      if (err.code === 'InvalidDeviceException' || err.code === 'MissingDeviceException') {
        setValidationError(err.message || 'Invalid device credentials.');
      } else if (err.code === 'UsernameExistsException' || err.code === 'AliasExistsException') {
        setValidationError('An account with this email already exists.');
      } else if (err.code === 'InvalidPasswordException') {
        setValidationError('Password does not meet requirements.');
      } else {
        setValidationError(err.message || 'Signup failed. Please try again.');
      }
    }
  };

  return (
    <ProviderPageLayout
      variant="split"
      splitImage="/images/hero-restaurant.png"
      splitEyebrow="Join ReTechSys"
      splitQuote="From counter to close-out — one portal for usage, insights, and a single, predictable invoice."
      splitAttribution="Trusted by cafés, restaurants & wellness venues"
    >
      <div className={`provider-card${step >= 2 ? ' provider-card--signup' : ' provider-card--auth'}`}>
        <StepProgress steps={SIGNUP_STEPS} currentStep={step} />

        {step === 1 && (
          <>
            <p className="provider-eyebrow">Get started</p>
            <h1 className="provider-title">Connect your device</h1>
            <p className="provider-subtitle">
              Enter the device credentials provided by ReTechSys to link your store.
            </p>

            {validationError && (
              <div className="provider-alert provider-alert--error">{validationError}</div>
            )}

            <form onSubmit={validateDevice}>
              <div className="provider-form-group">
                <label htmlFor="deviceId" className="provider-label">
                  Device ID <span className="provider-label-hint">— 4 characters, letters &amp; numbers</span>
                </label>
                <input
                  type="text"
                  id="deviceId"
                  value={deviceId}
                  onChange={(e) => setDeviceId(e.target.value)}
                  required
                  maxLength={4}
                  className="provider-input"
                  placeholder="e.g. a1d2"
                  autoComplete="off"
                />
                <p className="provider-field-help">
                  A short mix of letters and numbers, like <code>a1d2</code>.
                </p>
              </div>
              <div className="provider-form-group">
                <label htmlFor="deviceCode" className="provider-label">
                  Device code <span className="provider-label-hint">— 8 digits</span>
                </label>
                <input
                  type="text"
                  id="deviceCode"
                  value={deviceCode}
                  onChange={(e) => setDeviceCode(e.target.value)}
                  required
                  maxLength={8}
                  inputMode="numeric"
                  className="provider-input"
                  placeholder="e.g. 12345678"
                  autoComplete="off"
                />
                <p className="provider-field-help">
                  The 8-digit number that came with your device.
                </p>
              </div>
              <p className="provider-field-help provider-field-help--block">
                Both appear on your device screen during setup, or anytime under{' '}
                <strong>Settings&nbsp;→&nbsp;Device&nbsp;Info</strong>.
              </p>
              <button type="submit" className="provider-btn provider-btn--primary">
                Continue
              </button>
            </form>
          </>
        )}

        {step === 2 && (
          <>
            <p className="provider-eyebrow">Your account</p>
            <h1 className="provider-title">Create credentials</h1>
            <p className="provider-subtitle">
              Name and email only — we never ask for your home address here. Your card billing
              address is collected securely during payment setup.
            </p>

            {validationError && (
              <div className="provider-alert provider-alert--error">{validationError}</div>
            )}

            <form onSubmit={handleAccountStep}>
              <div className="provider-form-group">
                <label htmlFor="email" className="provider-label">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="provider-input"
                  placeholder="you@company.com"
                  autoComplete="email"
                />
              </div>

              <div className="provider-form-row">
                <div className="provider-form-group">
                  <label htmlFor="firstName" className="provider-label">First name</label>
                  <input
                    type="text"
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    className="provider-input"
                    autoComplete="given-name"
                  />
                </div>
                <div className="provider-form-group">
                  <label htmlFor="lastName" className="provider-label">Last name</label>
                  <input
                    type="text"
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    className="provider-input"
                    autoComplete="family-name"
                  />
                </div>
              </div>

              <PasswordField
                id="password"
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a strong password"
                autoComplete="new-password"
                showRules
              />

              <PasswordField
                id="confirmPassword"
                label="Confirm password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat your password"
                autoComplete="new-password"
                hint={
                  confirmPassword
                    ? password === confirmPassword
                      ? 'Passwords match'
                      : 'Passwords do not match'
                    : null
                }
              />

              <div className="provider-form-actions">
                <button type="button" className="provider-btn provider-btn--secondary" onClick={() => setStep(1)}>
                  Back
                </button>
                <button type="submit" className="provider-btn provider-btn--primary provider-btn--inline">
                  Continue
                </button>
              </div>
            </form>
          </>
        )}

        {step === 3 && (
          <>
            <p className="provider-eyebrow">Your venue</p>
            <h1 className="provider-title">Store location</h1>
            <p className="provider-subtitle">
              Where your business operates — used for regional billing setup. This is not your
              personal or card billing address; that is collected once during payment setup.
            </p>

            {validationError && (
              <div className="provider-alert provider-alert--error">{validationError}</div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="provider-form-group">
                <label htmlFor="storeName" className="provider-label">Store name</label>
                <input
                  type="text"
                  id="storeName"
                  value={storeName}
                  onChange={(e) => setStoreName(e.target.value)}
                  required
                  className="provider-input"
                  placeholder="Your café or wellness studio"
                  autoComplete="organization"
                />
              </div>

              <div className="provider-form-group">
                <label htmlFor="storeAddress" className="provider-label">Store street address</label>
                <input
                  type="text"
                  id="storeAddress"
                  value={storeAddress}
                  onChange={(e) => setStoreAddress(e.target.value)}
                  required
                  className="provider-input"
                  placeholder="123 Main Street"
                  autoComplete="off"
                />
              </div>

              <div className="provider-form-group">
                <label htmlFor="storeCountry" className="provider-label">Country</label>
                <select
                  id="storeCountry"
                  value={storeCountry}
                  onChange={(e) => setStoreCountry(e.target.value)}
                  required
                  className="provider-select"
                >
                  <option value="CA">Canada</option>
                  <option value="US">United States</option>
                </select>
              </div>

              <div className="provider-form-row">
                <div className="provider-form-group">
                  <label htmlFor="storeCity" className="provider-label">City</label>
                  <input
                    type="text"
                    id="storeCity"
                    value={storeCity}
                    onChange={(e) => setStoreCity(e.target.value)}
                    required
                    className="provider-input"
                    autoComplete="off"
                  />
                </div>
                <div className="provider-form-group">
                  <label htmlFor="storeState" className="provider-label">
                    {storeCountry === 'CA' ? 'Province' : 'State'}
                  </label>
                  <input
                    type="text"
                    id="storeState"
                    value={storeState}
                    onChange={(e) => setStoreState(e.target.value)}
                    required
                    className="provider-input"
                    placeholder={storeCountry === 'CA' ? 'ON' : 'NY'}
                    autoComplete="off"
                  />
                </div>
              </div>

              <div className="provider-form-row">
                <div className="provider-form-group">
                  <label htmlFor="storeZip" className="provider-label">
                    {storeCountry === 'CA' ? 'Postal code' : 'ZIP code'}
                  </label>
                  <input
                    type="text"
                    id="storeZip"
                    value={storeZip}
                    onChange={(e) => setStoreZip(e.target.value)}
                    required
                    className="provider-input"
                    placeholder={storeCountry === 'CA' ? 'A1A 1A1' : '90210'}
                    autoComplete="off"
                  />
                </div>
                <div className="provider-form-group">
                  <label htmlFor="storePhone" className="provider-label">
                    Phone <span className="provider-label-hint">(optional)</span>
                  </label>
                  <input
                    type="tel"
                    id="storePhone"
                    value={storePhone}
                    onChange={(e) => setStorePhone(e.target.value)}
                    className="provider-input"
                    placeholder="(555) 123-4567"
                    autoComplete="tel"
                  />
                </div>
              </div>

              <p className="provider-field-help provider-field-help--block">
                By creating an account you agree to the{' '}
                <button
                  type="button"
                  className="provider-link"
                  onClick={() => {
                    sessionStorage.setItem('signupStep', step.toString());
                    persistDraft();
                    navigate('/provider/terms', { state: { from: '/provider/signup' } });
                  }}
                >
                  Terms and Conditions
                </button>
                . You'll confirm your per-transaction rate and authorize monthly billing during
                payment setup.
              </p>

              <div className="provider-form-actions">
                <button type="button" className="provider-btn provider-btn--secondary" onClick={() => setStep(2)}>
                  Back
                </button>
                <button type="submit" className="provider-btn provider-btn--primary provider-btn--inline">
                  Create account
                </button>
              </div>
            </form>
          </>
        )}

        <div className="provider-footer-links">
          <p style={{ margin: 0, fontSize: 14, color: 'var(--color-text-muted)' }}>
            Already have an account?{' '}
            <button type="button" className="provider-link" onClick={() => navigate('/provider/login')}>
              Sign in
            </button>
          </p>
        </div>
      </div>
    </ProviderPageLayout>
  );
};

export default ProviderSignup;
